import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { TokenManager } from '../../../shared/utils/token-manager';

interface Alert {
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
}

export const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const { signIn } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [alert, setAlert] = useState<Alert>({ type: 'success', message: '' });
    const [showAlert, setShowAlert] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

    const validate = (): boolean => {
        const newErrors: { email?: string; password?: string } = {};

        if (!email) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        setShowAlert(false);

        try {
            const response = await signIn({ email, password });
            const accessToken = localStorage.getItem('accessToken');

            if (accessToken) {
                navigate('/dashboard');
            } else {
                console.error('Access token not found in localStorage');
                console.error('Response structure:', response);
                setIsLoading(false);
                setShowAlert(true);
                setAlert({
                    type: 'error',
                    message: 'Login failed: Token not received'
                });
            }
        } catch (error: any) {
            setIsLoading(false);

            // Reset form
            setPassword('');

            // Extract error message from API response
            let errorMessage = 'Wrong email or password';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlert({
                type: 'error',
                message: errorMessage
            });
            setShowAlert(true);
        }
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            height: '52px',
            border: '1px solid #e2e8f0',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                border: '1px solid #6366f1'
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            padding: '12px 14px',
            fontSize: '15px'
        },
        '& .MuiFormHelperText-root': {
            color: '#ef4444',
            marginTop: '4px'
        }
    };

    return (
        <div className="min-h-screen bg-[#F0F4F8] flex flex-col items-center justify-center p-4">
            <div className="bg-[#DCE7F6] rounded-[32px] shadow-lg w-full max-w-md px-10 py-12">
                {/* Logo */}
                <div className="flex justify-start mb-8 ml-[-10px]">
                    <img
                        src="/assets/images/logo/logo.svg"
                        alt="codeflix invoice Logo"
                        className="h-16"
                    />
                </div>

                {/* Title */}
                <div className="text-left mb-10">
                    <h1 className="text-[#102A43] text-[40px] font-extrabold leading-tight tracking-tight">Sign in</h1>
                </div>

                {/* Alert */}
                {showAlert && (
                    <div className={`mb-6 p-4 rounded-xl text-sm ${alert.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>
                        {alert.message}
                    </div>
                )}

                {/* Sign in form */}
                <form onSubmit={handleSubmit}>
                    {/* Email field */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-[#1f2937] mb-2">Email address *</label>
                        <TextField
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isLoading}
                            fullWidth
                            required
                            sx={fieldStyles}
                        />
                    </div>

                    {/* Password field */}
                    <div className="mb-10">
                        <label className="block text-sm font-medium text-[#1f2937] mb-2">Password *</label>
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={isLoading}
                            fullWidth
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{ color: '#94A3B8' }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={fieldStyles}
                        />
                    </div>

                    {/* Submit button */}
                    <AppButton
                        type="submit"
                        variant="primary"
                        fullWidth
                        loading={isLoading}
                        sx={{
                            py: 2,
                            borderRadius: '25px',
                            fontSize: '18px',
                            fontWeight: 700,
                            textTransform: 'none',
                            backgroundColor: '#6366f1',
                            boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.3)',
                            '&:hover': {
                                backgroundColor: '#4f46e5',
                            }
                        }}
                    >
                        Sign in
                    </AppButton>
                </form>
            </div>
        </div>
    );
};
